import { SummaryMode } from './types'
import type { MetricSeries, TimeSeriesDataPoint } from './types'

const deriveCumulative = (
  data: TimeSeriesDataPoint[],
): TimeSeriesDataPoint[] => {
  const baseline = data.find((p) => p.value != null)?.value ?? null
  return data.map((point) => ({
    date: point.date,
    value:
      point.value != null && baseline != null ? point.value - baseline : null,
  }))
}

/** Derive a cumulative "net new followers" series from a cumulative followers series.
 * Each point = followers gained since the first data point in the period, so the
 * line starts at 0, trends over time, and ends at the total net new for the period.
 * This makes the last point match the summary panel value and tells a cleaner
 * growth story than noisy day-over-day spikes. */
export function computeNetNewSeries(source: MetricSeries): MetricSeries {
  return {
    key: 'followers-net-new',
    title: 'Net New Followers',
    icon: source.icon,
    color: 'var(--chart-color-followers-net-new)',
    // 'latest' so the summary shows the last point (= total net new for the period)
    // and the comparison delta is expressed as a percentage.
    summaryMode: SummaryMode.Latest,
    data: deriveCumulative(source.data),
    comparisonData: source.comparisonData
      ? deriveCumulative(source.comparisonData)
      : undefined,
  }
}

/**
 * Expands visible metrics with their comparison-period siblings so the chart
 * renders an additional dashed line per metric when comparison data is present.
 * Comparison points are positionally re-aligned onto the primary axis
 * (index N of comparison → index N of primary), with the real date preserved on
 * `originalDate` for tooltip + axis-label rendering.
 * The `viewMode` toggle gates which series go into the chart.
 *
 * @see ../MetricsNarrativeChart/MetricsNarrativeChart.tsx
 */
export function buildChartSeries(
  visibleMetrics: MetricSeries[],
  viewMode: 'primary' | 'comparison' | 'both',
): MetricSeries[] {
  const result: MetricSeries[] = []
  for (const m of visibleMetrics) {
    if (viewMode !== 'comparison') {
      result.push(m)
    }
    if (
      viewMode !== 'primary' &&
      m.comparisonData &&
      m.comparisonData.length > 0
    ) {
      const alignedLength = Math.min(m.data.length, m.comparisonData.length)
      const alignedData: TimeSeriesDataPoint[] = []
      for (let i = 0; i < alignedLength; i++) {
        const primaryPoint = m.data[i]
        const comparisonPoint = m.comparisonData[i]
        alignedData.push({
          date: primaryPoint.date,
          value: comparisonPoint.value ?? null,
          originalDate: comparisonPoint.date,
        })
      }
      result.push({
        ...m,
        key: `${m.key}-comparison`,
        title: `${m.title} (comparison)`,
        data: alignedData,
        comparisonData: undefined,
        dashed: true,
      })
    }
  }
  return result
}
